// hooks/use-dashboard-drivers.ts
import { FormEvent, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IDocsDriver } from '../../../utils/types';
import { useGetAllDrivers } from '../../../apis/drivers/get-all-drivers';
import { useGetAllStates } from '../../../apis/users/get-all-states';
import { useBlockDriver } from '../../../apis/drivers/block-drivers';
import useVehicleTypes from '../../useVehicleTypes';
import { areBothDatesPresent, isOneDatePresent } from '../../../utils/helpers';
import { VERIFICATION_STATUS } from '../../../utils/Enums';

const useDashboardDrivers = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedDriver, setSelectedDriver] = useState<IDocsDriver | null>(null);
    const [isAddDriverOpen, setIsAddDriverOpen] = useState(false);
    const [isBlockModalVisible, setIsBlockModalVisible] = useState(false);
    const [zoomed, setZoomed] = useState(false);
    const [image, setImage] = useState("");
    const [key, setKey] = useState(0);

    const filterFormRef = useRef<HTMLFormElement>(null);
    const searchFormRef = useRef<HTMLFormElement>(null);

    const getParam = (key: string) => searchParams.get(key) || "";

    const search = getParam("search");
    const status = getParam("status");
    const secondStatus = getParam("secondStatus");
    const onlineStatus = getParam("onlineStatus");
    const fromDate = getParam("fromDate");
    const toDate = getParam("toDate");
    const state = getParam("state");

    const buildParams = () => {
        const params: Record<string, string | number> = {
            limit: 10,
            page: getParam("page") || "1",
            searchKey: "email",
            search,
            verificationStatus: status,
            fromDate,
            toDate,
            online: onlineStatus,
            state,
        };

        if (secondStatus) {
            const [field, value] = secondStatus.split("-");
            params[field] = value;
        }

        return params;
    };

    const { data: driversData, isFetching: loading, refetch: refetchDrivers } = useGetAllDrivers(buildParams());
    const { data: statesData, isFetching: isLoadingStates } = useGetAllStates();
    const { mutateAsync: blockDriver, isPending: isBlocking } = useBlockDriver();
    const { getVehicleLabel } = useVehicleTypes();

    const drivers = driversData?.docs ?? [];
    const totalPages = driversData?.totalPages ?? 0;
    const allStates = statesData?.states ?? [];

    const updateUrlParams = (newParams: Record<string, string>) => {
        const updatedParams = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, value]) => {
            value ? updatedParams.set(key, value) : updatedParams.delete(key);
        });
        setSearchParams(updatedParams);
    };

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        updateUrlParams({ search, status, secondStatus, onlineStatus, fromDate, toDate, state, page: "1" });
    };

    const handleResetFilters = () => {
        setSearchParams({});
        setKey((prev) => prev + 1);
        searchFormRef.current?.reset();
    };

    const handleOpenBlockModal = (driver: IDocsDriver) => {
        setSelectedDriver(driver);
        setIsBlockModalVisible(true);
    };

    const handleBlock = async () => {
        if (!selectedDriver) return;
        await blockDriver({ id: selectedDriver._id, blocked: !selectedDriver.isBlocked });
        refetchDrivers();
    };

    const handleZoomImage = (src: string) => {
        setImage(src);
        setZoomed(true);
    };

    const isAnyFilterApplied = status !== "" || secondStatus !== "" || onlineStatus !== "" || state !== "";
    const isApplyButtonDisabled = isAnyFilterApplied
        ? isOneDatePresent(fromDate, toDate)
        : !areBothDatesPresent(fromDate, toDate);
    const isClearButtonDisabled = !search && !status && !secondStatus && !onlineStatus && !fromDate && !toDate && !state;

    const verificationOptions = [
        { label: "Approved", value: VERIFICATION_STATUS.APPROVED },
        { label: "Pending review", value: VERIFICATION_STATUS.PENDING_REVIEW },
        { label: "Resubmitted", value: VERIFICATION_STATUS.RESUBMIT },
        { label: "Rejected", value: VERIFICATION_STATUS.REJECTED },
    ];

    const secondStatusOptions = [
        { label: "Email Verified", value: "emailVerified-1" },
        { label: "Email Not Verified", value: "emailVerified-0" },
        { label: "Blocked", value: "blocked-1" },
    ];

    const onlineStatusOptions = [
        { label: "Online", value: "1" },
        { label: "Offline", value: "0" },
    ];

    return {
        drivers,
        totalPages,
        loading,
        selectedDriver,
        isAddDriverOpen,
        setIsAddDriverOpen,
        isBlockModalVisible,
        setIsBlockModalVisible,
        isBlocking,
        zoomed,
        setZoomed,
        image,
        key,
        search,
        status,
        secondStatus,
        onlineStatus,
        fromDate,
        toDate,
        state,
        allStates,
        isLoadingStates,
        searchParams,
        filterFormRef,
        searchFormRef,
        getVehicleLabel,
        updateUrlParams,
        handleSearch,
        handleResetFilters,
        handleOpenBlockModal,
        handleBlock,
        handleZoomImage,
        isApplyButtonDisabled,
        isClearButtonDisabled,
        refetchDrivers,
        verificationOptions,
        secondStatusOptions,
        onlineStatusOptions,
    };
};

export default useDashboardDrivers;